import React, { useEffect, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Phone,
  Mail,
  User,
  FileText,
  BookUser,
  Sparkles,
  Search
} from 'lucide-react';
import { employeeContactsAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const defaultForm = {
  name: '',
  contact: '',
  description: '',
  phone: '',
  email: ''
};

const EmployeeContacts = () => {
  const { isAdmin, isManager } = useAuth();
  const canManage = isAdmin() || isManager();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await employeeContactsAPI.list();
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger les contacts');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (!form.phone.trim()) {
      toast.error('Le téléphone est requis');
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact: form.contact.trim() || null,
        description: form.description.trim() || null,
        phone: form.phone.trim(),
        email: form.email.trim() || null
      };
      if (editingId) {
        await employeeContactsAPI.update(editingId, payload);
        toast.success('Contact modifié');
      } else {
        await employeeContactsAPI.create(payload);
        toast.success('Contact ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.message;
      toast.error(msg || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitLoading(false);
    }
  };

  const openCreate = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      name: row.name || '',
      contact: row.contact || '',
      description: row.description || '',
      phone: row.phone || '',
      email: row.email || ''
    });
    setEditingId(row.id);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Supprimer le contact « ${row.name} » ?`)) return;
    try {
      await employeeContactsAPI.remove(row.id);
      toast.success('Contact supprimé');
      load();
    } catch {
      toast.error('Suppression impossible');
    }
  };

  const filtered = items.filter((c) => {
    const q = searchTerm.toLowerCase();
    return (
      String(c.name || '').toLowerCase().includes(q) ||
      String(c.contact || '').toLowerCase().includes(q) ||
      String(c.description || '').toLowerCase().includes(q) ||
      String(c.phone || '').includes(searchTerm) ||
      String(c.email || '').toLowerCase().includes(q)
    );
  });

  const initialLetter = (name) => {
    const s = String(name || '').trim();
    return s ? s.charAt(0).toUpperCase() : '?';
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pb-10 sm:pb-12">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-primary-600 via-primary-700 to-slate-900 text-white shadow-xl shadow-primary-900/20 mb-6 sm:mb-8">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent" />
        <div className="absolute bottom-0 right-0 w-40 h-40 sm:w-64 sm:h-64 rounded-full bg-white/5 blur-3xl -mr-12 -mb-12" />
        <div className="relative px-4 py-7 sm:px-8 sm:py-10 md:py-11">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="flex items-start gap-4 max-w-2xl">
              <div className="flex-shrink-0 p-3 sm:p-3.5 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20">
                <BookUser className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-primary-100 text-xs sm:text-sm font-medium uppercase tracking-wider mb-1">
                  Annuaire interne
                </p>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                  Contacts
                </h1>
                <p className="mt-2 sm:mt-3 text-sm sm:text-base text-primary-100/95 leading-relaxed max-w-xl">
                  Coordonnées utiles pour l&apos;équipe : nom, contact, description, téléphone et e-mail optionnel.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:shrink-0">
              <div className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm text-sm">
                <User className="w-4 h-4 opacity-90" />
                <span className="font-semibold tabular-nums">{items.length}</span>
                <span className="text-primary-100 text-xs sm:text-sm">
                  {items.length <= 1 ? 'contact' : 'contacts'}
                </span>
              </div>
              {canManage && (
                <ModernButton
                  variant="secondary"
                  className="!bg-white !text-primary-700 hover:!bg-primary-50 border-0 shadow-lg w-full sm:w-auto justify-center"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={openCreate}
                >
                  Ajouter un contact
                </ModernButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
        <div className="w-full sm:max-w-md sm:flex-1">
          <ModernSearchBar
            placeholder="Rechercher par nom, téléphone, e-mail…"
            onSearch={setSearchTerm}
            className="w-full"
            debounce={300}
          />
        </div>
        <p className="text-sm text-neutral-500 shrink-0 flex items-center gap-2">
          <Search className="w-4 h-4 opacity-60" />
          <span>
            <span className="font-semibold text-neutral-800 tabular-nums">{filtered.length}</span>
            {' '}
            résultat{filtered.length <= 1 ? '' : 's'}
          </span>
        </p>
      </div>

      {/* Liste */}
      {items.length === 0 ? (
        <ModernCard className="p-8 sm:p-12 md:p-14 text-center border-2 border-dashed border-neutral-200 bg-gradient-to-b from-neutral-50/80 to-white">
          <div className="inline-flex p-4 rounded-2xl bg-primary-50/80 mb-4">
            <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-primary-500" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-neutral-900">Aucun contact enregistré</h2>
          <p className="text-neutral-600 mt-2 text-sm sm:text-base max-w-md mx-auto">
            Ajoutez des numéros et adresses utiles pour que toute l&apos;équipe les retrouve facilement.
          </p>
          {canManage && (
            <ModernButton variant="primary" className="mt-6 sm:mt-8" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
              Ajouter un contact
            </ModernButton>
          )}
        </ModernCard>
      ) : filtered.length === 0 ? (
        <ModernCard className="p-10 sm:p-12 text-center border border-neutral-200/90 bg-white">
          <div className="inline-flex p-3 rounded-xl bg-neutral-100 mb-3">
            <Search className="w-8 h-8 text-neutral-500" />
          </div>
          <p className="text-neutral-700 font-medium">Aucun résultat pour cette recherche</p>
          <p className="text-sm text-neutral-500 mt-1">Essayez d&apos;autres mots-clés.</p>
        </ModernCard>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 list-none p-0 m-0">
          {filtered.map((c) => (
            <li key={c.id} className="h-full">
              <article
                className={[
                  'group h-full flex flex-col rounded-2xl sm:rounded-3xl border bg-white overflow-hidden',
                  'border-neutral-200/80 shadow-sm ring-1 ring-black/[0.03]',
                  'hover:shadow-lg hover:border-primary-200/70 hover:ring-primary-500/10',
                  'transition-all duration-300 ease-out'
                ].join(' ')}
              >
                <div className="p-4 sm:p-5 flex flex-col flex-1 min-h-0">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div
                      className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-lg sm:text-xl font-bold shadow-md shadow-primary-900/20"
                      aria-hidden
                    >
                      {initialLetter(c.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-semibold text-neutral-900 text-base sm:text-lg leading-snug tracking-tight group-hover:text-primary-800 transition-colors line-clamp-2">
                        {c.name}
                      </h2>
                      {c.contact && (
                        <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{c.contact}</p>
                      )}
                    </div>
                  </div>

                  {c.description?.trim() ? (
                    <div className="mt-4 rounded-xl border border-neutral-100 bg-neutral-50/90 px-3 py-2.5 sm:px-3.5 sm:py-3">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400 mb-1">Description</p>
                      <p className="text-sm text-neutral-700 line-clamp-4 whitespace-pre-wrap leading-relaxed">{c.description}</p>
                    </div>
                  ) : (
                    <div className="mt-4 text-sm text-neutral-400 italic">Pas de description</div>
                  )}

                  <div className="mt-4 space-y-2.5">
                    <a
                      href={`tel:${c.phone}`}
                      className="flex items-center gap-2.5 rounded-xl bg-primary-50/80 border border-primary-100/80 px-3 py-2.5 text-sm font-medium text-primary-900 hover:bg-primary-100/80 transition-colors"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                        <Phone className="w-4 h-4 text-primary-600" />
                      </span>
                      <span className="truncate">{c.phone || '—'}</span>
                    </a>
                    {c.email ? (
                      <a
                        href={`mailto:${c.email}`}
                        className="flex items-center gap-2.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-2.5 text-sm text-neutral-800 hover:border-primary-200 hover:bg-neutral-50/90 transition-colors"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-100">
                          <Mail className="w-4 h-4 text-neutral-600" />
                        </span>
                        <span className="truncate min-w-0">{c.email}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-neutral-400 pl-1">
                        <Mail className="w-4 h-4 shrink-0 opacity-50" />
                        <span>Pas d&apos;e-mail</span>
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <div className="mt-5 pt-4 border-t border-neutral-100 flex flex-col sm:flex-row gap-2">
                      <ModernButton
                        variant="primary"
                        size="sm"
                        className="flex-1 !justify-center min-h-[2.5rem]"
                        icon={<Edit className="w-4 h-4 shrink-0" />}
                        onClick={() => openEdit(c)}
                      >
                        Modifier
                      </ModernButton>
                      <ModernButton
                        variant="danger"
                        size="sm"
                        className="flex-1 !justify-center min-h-[2.5rem]"
                        icon={<Trash2 className="w-4 h-4 shrink-0" />}
                        onClick={() => handleDelete(c)}
                      >
                        Supprimer
                      </ModernButton>
                    </div>
                  )}
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}

      <ModernModal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setForm(defaultForm);
          setEditingId(null);
        }}
        title={editingId ? 'Modifier le contact' : 'Nouveau contact'}
        size="xxl"
      >
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <div className="md:col-span-2">
              <ModernInput
                label="Nom"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="Nom complet ou raison sociale"
              />
            </div>
            <div className="md:col-span-2">
              <ModernInput
                label="Contact"
                name="contact"
                value={form.contact}
                onChange={handleChange}
                placeholder="Fonction, service, société… (optionnel)"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                className="w-full min-h-[100px] sm:min-h-[120px] px-3 py-2.5 border border-neutral-300 rounded-xl text-sm sm:text-base text-neutral-800 placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow resize-y"
                placeholder="Notes, horaires, précisions… (optionnel)"
              />
            </div>
            <ModernInput
              label="Téléphone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              placeholder="Ex. : 06 12 34 56 78"
            />
            <ModernInput
              label="E-mail (optionnel)"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="contact@exemple.com"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-neutral-100 sticky bottom-0 bg-white/95 backdrop-blur-sm -mx-1 px-1 pb-1 sm:static sm:bg-transparent sm:backdrop-none">
            <ModernButton
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => {
                setShowForm(false);
                setForm(defaultForm);
                setEditingId(null);
              }}
            >
              Annuler
            </ModernButton>
            <ModernButton type="submit" variant="primary" className="w-full sm:w-auto" disabled={submitLoading}>
              {submitLoading ? 'Enregistrement…' : editingId ? 'Enregistrer' : 'Créer'}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default EmployeeContacts;
